/**
 * WHAT A PERSON IS SENT, IN THE CARD'S WORDS (ops #76 phase 2, phase 4 of `docs/plan-preferences.md`,
 * section 8). The office's read answers each person's effective choices, the holds that stand and the
 * guard; this says them, one sentence a thing, and draws nothing. The server decides every one of them
 * again at composing, so these words are UX and never the control.
 */
import {
  PREFERENCE_CATEGORIES,
  PREFERENCE_CATEGORY_LABELS,
  SEND_TIME_LABELS,
  type PreferenceCategory,
  type PreferenceHow,
} from '@fk/shared';
import type {
  ChoiceSetting,
  HeldPeriod,
  MessageHold,
  MessagePause,
  PersonChoicesView,
  QuietHours,
} from './api.ts';
import { OFFICE_CALLS_LINE } from './choices.ts';
import { dayOf, formatDay } from './format.ts';

/** The guard, in the contract's words, as their page at home says it of them. */
export const GUARD_LINE = OFFICE_CALLS_LINE;

/** The kinds the office may hold: every one a message files under, the account's never. */
export const HOLDABLE_CATEGORIES: readonly PreferenceCategory[] = PREFERENCE_CATEGORIES.filter(
  (category) => category !== 'home_checks' && category !== 'reviews',
);

/** What a row is about. */
export function categoryLabel(category: PreferenceCategory | 'account'): string {
  return category === 'account' ? 'About the account' : PREFERENCE_CATEGORY_LABELS[category];
}

/** Where their email stands, from the register. */
export function emailWords(person: PersonChoicesView): string {
  switch (person.email.state) {
    case 'none':
      return 'Email: no address on file.';
    case 'marked':
      return 'Email: marked do not contact.';
    case 'no_consent':
      return 'Email: no yes to email on record yet, so none goes.';
    case 'bouncing':
      return 'Email: their address is bouncing.';
    case 'on':
      return 'Email: on.';
  }
}

/**
 * WHERE THEIR TEXTS STAND, from the register and never from a row: a row's text is never the
 * office's to switch. Read-only, with its reason.
 */
export function textsWords(person: PersonChoicesView): string {
  switch (person.texts.state) {
    case 'none':
      return 'Texts: no number on file.';
    case 'off_by_stop':
      return 'Texts: stopped by them.';
    case 'marked':
      return 'Texts: marked do not contact.';
    case 'off_by_choice':
      return 'Texts: they turned them off.';
    case 'no_consent':
      return 'Texts: no yes on the website box for their number.';
    case 'on':
      return person.textsReach ? 'Texts: on.' : 'Texts: on, but ops is not sending texts yet.';
  }
}

/** A reminder's lead in days, as a choice of the form says it. */
export function leadWords(days: number): string {
  if (days === 0) return 'No reminder';
  return `${days} ${days === 1 ? 'day' : 'days'} ahead`;
}

/** One row of theirs: email on or off, by text too, and the reminder's lead and time. */
export function settingWords(row: ChoiceSetting): string {
  const parts: string[] = [row.email === 'on' ? 'Email on' : 'Email off'];
  if (row.text) parts.push('and by text');
  if (row.category === 'visit_reminders' && row.leadDays !== null) {
    parts.push(
      row.leadDays === 0
        ? 'no reminder'
        : `${leadWords(row.leadDays).toLowerCase()}${
            row.sendTime ? `, ${SEND_TIME_LABELS[row.sendTime].toLowerCase()}` : ''
          }`,
    );
  }
  if (row.category === 'account') parts.push('always');
  return `${parts.join(', ')}.`;
}

/** How a row was written, after the day. */
const HOW_WORDS: Readonly<Record<PreferenceHow, string>> = Object.freeze({
  home: 'at home',
  call: 'on a call',
  email: 'by email',
  text: 'by text',
  in_person: 'in person',
  stop: 'by their STOP',
  start: 'by their START',
  lock: 'by the office',
});

/** Who changed a row last, or null where nobody chose and the household's defaults answer. */
export function changedWords(row: ChoiceSetting): string {
  if (row.changed === null) return 'Nobody has chosen: the household defaults.';
  const day = formatDay(dayOf(row.changed.at));
  const how = HOW_WORDS[row.changed.how];
  if (row.changed.by === 'self') return `Chosen by them ${how}, ${day}.`;
  if (row.changed.by === 'office') return `Recorded by the office, as they asked ${how}, ${day}.`;
  return `Set ${how}, ${day}.`;
}

/** A period the office holds a kind for, from the day it holds or the day it will. */
export function heldWords(held: HeldPeriod, today: string): string {
  const begun = held.from <= today;
  const until = held.until === null ? 'until it is lifted' : `until ${formatDay(held.until)}`;
  return begun
    ? `Held by the office ${until}.`
    : `Held by the office from ${formatDay(held.from)} ${until}.`;
}

/** What a pause silences and what it never does, the plan's section 14 said of them. */
export const PAUSE_SILENCES_LINE =
  'No reminders, visit news, home check reports or receipts. Changes to a visit and invoices still go.';

/**
 * THEIR PAUSE, from the day it holds or the day it will, at every home or one (phase 5), with what it
 * silences; or that nothing is paused.
 */
export function pauseWords(
  paused: MessagePause | null | undefined,
  homeOf: (homeId: string) => string,
  today: string,
): string {
  if (!paused) return 'Messages: not paused.';
  const where = paused.homeId === null ? 'at every home' : `at ${homeOf(paused.homeId)}`;
  const until = paused.until === null ? 'until it is ended' : `until ${formatDay(paused.until)}`;
  const when =
    paused.from <= today
      ? `Paused ${where} ${until}.`
      : `Paused ${where} from ${formatDay(paused.from)} ${until}.`;
  return `${when} ${PAUSE_SILENCES_LINE}`;
}

/** Their quiet hours, which bind a text alone (phase 5), or that texts go within the texting hours. */
export function quietWords(quietHours: QuietHours | null | undefined): string {
  return quietHours
    ? `Texts only between ${quietHours.from} and ${quietHours.until}. Email goes at any hour.`
    : 'Quiet hours: none, so a text goes whenever the texting hours allow.';
}

/** A hold that stands, on the household's list of them. */
export function holdWords(
  hold: MessageHold,
  nameOf: (personId: string) => string,
  homeOf: (homeId: string) => string,
): string {
  const what =
    hold.category === null ? "Every message but the account's" : categoryLabel(hold.category);
  const whose = hold.personId === null ? 'for the household' : `for ${nameOf(hold.personId)}`;
  const where = hold.homeId === null ? 'at every home' : `at ${homeOf(hold.homeId)}`;
  const until = hold.until === null ? 'until it is lifted' : `until ${formatDay(hold.until)}`;
  return `${what}, ${whose}, ${where}, from ${formatDay(hold.from)} ${until}.`;
}
